import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";

export type FieldColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
};

export const AIF_CONSOLIDATED_REPORT = "Consolidated Report";

export const AIF_TABLE_SEGMENT = "AIF";

export const AIF_REPORT_TYPE_OPTIONS = [
  { value: "Holding", label: "Unrealized" },
  { value: "Realized", label: "Realized" },
  { value: "All", label: "All" },
  { value: "Transaction", label: "Transaction" },
  { value: AIF_CONSOLIDATED_REPORT, label: AIF_CONSOLIDATED_REPORT },
] as const;

export const AIF_QUICK_REPORTS = [
  { id: "holding", label: "Unrealized", reportType: "Holding" },
  { id: "realized", label: "Realized", reportType: "Realized" },
  { id: "all", label: "All", reportType: "All" },
  { id: "transaction", label: "Transaction", reportType: "Transaction" },
  { id: "consolidated", label: "Consolidated", reportType: AIF_CONSOLIDATED_REPORT },
];

export const INVESTOR_COLUMN: FieldColumn = {
  key: "investorDisplay",
  value: "Investor Name",
  locked: true,
  minWidth: 200,
};

/** aumsampaan AIF Holding preset (modern report panel) */
export const AIF_UNREALIZED_HOLDING_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "averagePrice", value: "Purchase NAV", minWidth: 112 },
  { key: "currentPrice", value: "Current NAV", minWidth: 112 },
  { key: "amount", value: "Invested Amt.", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPerc", value: "P&L %", minWidth: 96 },
];

export const AIF_REALIZED_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Units Sold", minWidth: 100 },
  { key: "purchaseNAV", value: "Purchase NAV", minWidth: 112 },
  { key: "currentPrice", value: "Current NAV", minWidth: 112 },
  { key: "avgBuyPrice", value: "Avg Buy Price", minWidth: 112 },
  { key: "saleValue", value: "Sale Value", minWidth: 128 },
  { key: "realisedPnl", value: "Realized P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realized P&L%", minWidth: 120 },
];

export const AIF_TRANSACTION_COLUMNS: FieldColumn[] = [
  { key: "transType", value: "Type", locked: true, minWidth: 120 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "purchasePrice", value: "Price", minWidth: 112 },
  { key: "currentPrice", value: "Current NAV", minWidth: 112 },
  { key: "amount", value: "Amount", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
];

export const AIF_ALL_COMMON_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "folioNumber", value: "Folio Number", minWidth: 120 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "averageNav", value: "Purchase NAV", minWidth: 112 },
  { key: "currentNAV", value: "Current NAV", minWidth: 112 },
  { key: "costValue", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

/** aumsampaan AIF_CONSOLIDATED_VISIBLE_FIELD_TITLES */
export const AIF_CONSOLIDATED_COLUMNS: FieldColumn[] = [
  { key: "name", value: "Fund Name", locked: true, minWidth: 280 },
  { key: "vintage", value: "Vintage", minWidth: 90, align: "center" },
  { key: "commitment", value: "Commitment", minWidth: 140, align: "right" },
  { key: "unfundedCommitment", value: "Unfunded Commitment", minWidth: 150, align: "right" },
  { key: "updatedDate", value: "Updated Date", minWidth: 118, align: "center" },
  { key: "amountCalled", value: "Amount Called", minWidth: 140, align: "right" },
  { key: "calledPercentage", value: "Called %", minWidth: 92, align: "right" },
  { key: "amountDistributed", value: "Amount Distributed", minWidth: 150, align: "right" },
  { key: "distributedPercentage", value: "Distributed %", minWidth: 104, align: "right" },
  { key: "recallableDistributions", value: "Recallable Distributions", minWidth: 150, align: "right" },
  { key: "balanceRecallableCapital", value: "Balance Recallable Capital", minWidth: 168, align: "right" },
  { key: "investedCost", value: "Invested Cost", minWidth: 130, align: "right" },
  { key: "valuation", value: "Valuation", minWidth: 130, align: "right" },
  { key: "valuationDate", value: "Valuation Date", minWidth: 118, align: "center" },
  { key: "valuationPercentage", value: "Valuation %", minWidth: 104, align: "right" },
  {
    key: "valuationPlusDistributionPercentage",
    value: "(Val + Dist.) as % of Called",
    minWidth: 170,
    align: "right",
  },
  { key: "income", value: "Income", minWidth: 110, align: "right" },
  { key: "expenses", value: "Expense", minWidth: 110, align: "right" },
  { key: "irr", value: "IRR %", minWidth: 84, align: "right" },
  { key: "dpi", value: "DV/PI", minWidth: 84, align: "right" },
  { key: "rvpi", value: "TV/PI", minWidth: 84, align: "right" },
];

export const getColumnsForAifReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return AIF_UNREALIZED_HOLDING_COLUMNS;
    case "Realized":
      return AIF_REALIZED_COLUMNS;
    case "All":
      return AIF_ALL_COMMON_COLUMNS;
    case "Transaction":
      return AIF_TRANSACTION_COLUMNS;
    case AIF_CONSOLIDATED_REPORT:
      return AIF_CONSOLIDATED_COLUMNS;
    default:
      return AIF_UNREALIZED_HOLDING_COLUMNS;
  }
};

export const isAifConsolidatedReport = (reportType: string): boolean =>
  reportType === AIF_CONSOLIDATED_REPORT;

export const reportTypeNeedsDateRange = (reportType: string): boolean =>
  reportType === "Holding";
